import { useT } from '../i18n';
import { formatMoney } from '../lib/money';
import { Button } from './ui/Button';
import { StatusPill } from './ui/StatusPill';

export type ExpenseRowStatus = 'synced' | 'pending' | 'syncing' | 'failed';

export interface ExpenseRowProps {
  descricao: string;
  valor: string;
  photoUrl?: string | undefined;
  status: ExpenseRowStatus;
  errorMessage?: string | undefined;
  onRetry?: (() => void) | undefined;
  onDiscard?: (() => void) | undefined;
}

const STATUS_TONE: Record<ExpenseRowStatus, 'success' | 'accent' | 'danger'> = {
  synced: 'success',
  pending: 'accent',
  syncing: 'accent',
  failed: 'danger',
};

export function ExpenseRow({
  descricao,
  valor,
  photoUrl,
  status,
  errorMessage,
  onRetry,
  onDiscard,
}: ExpenseRowProps) {
  const { t } = useT();
  const tone = STATUS_TONE[status];

  return (
    <div className="flex flex-col gap-2 border-b border-line py-3 last:border-b-0">
      <div className="flex items-center gap-3">
        {photoUrl ? (
          <img
            src={photoUrl}
            alt=""
            className="h-11 w-11 shrink-0 rounded-md border border-line object-cover"
          />
        ) : (
          <div
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-surface-raised text-ink-faint"
            aria-hidden="true"
          >
            <span className="text-xs">{t('expenses.noPhoto')}</span>
          </div>
        )}

        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] text-ink">{descricao}</p>
          <StatusPill tone={tone} dot={status === 'syncing'} pulse={status === 'syncing'}>
            {t(`expenses.status.${status}`)}
          </StatusPill>
        </div>

        <p className="tabular-money shrink-0 text-[15px] font-medium text-ink">
          {formatMoney(valor)}
        </p>
      </div>

      {status === 'failed' ? (
        <div className="flex items-center gap-2 pl-14">
          {errorMessage ? (
            <p className="flex-1 truncate text-xs text-danger">{errorMessage}</p>
          ) : null}
          {onRetry ? (
            <Button variant="secondary" onClick={onRetry} className="min-h-0 px-3 py-1.5 text-xs">
              {t('common.retry')}
            </Button>
          ) : null}
          {onDiscard ? (
            <Button variant="ghost" onClick={onDiscard} className="min-h-0 px-3 py-1.5 text-xs">
              {t('expenses.discard')}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
